import { fetchApi } from "@inmagik/react-crud"
import { Alert, Box, Button, TextField, Typography } from "@mui/material"
import { Formik, getIn } from "formik"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import * as yup from "yup"
import { API_URL } from "../../constants"

export function ResetPassword() {
  const [confirm, setConfirm] = useState(false)
  // get token from qs
  const token = new URLSearchParams(window.location.search).get("token")
  const { t } = useTranslation()
  const schema = useMemo(() => {
    return yup.object().shape({
      password: yup
        .string()
        .required(t("activate_account.schema_password_required"))
        .min(8, t("activate_account.schema_password_min", { number: 8 })),
      repeatPassword: yup
        .string()
        .required(t("activate_account.schema_repeat_password_required"))
        .oneOf([yup.ref("password"), ""], t("activate_account.schema_repeat_password_one")),
    })
  }, [t])
  const initialValues = {
    password: "",
    repeatPassword: "",
  }
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        p={3}
        borderRadius={1}
        boxShadow={3}
        minWidth={395}
        maxWidth={464}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={1} marginBottom={4} width="100%">
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              textAlign: "center",
              color: "rgba(0, 0, 0, 0.87)",
            }}
          >
            {t("reset_password.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "15px",
              lineHeight: "133.4%",
              color: "#757575",
            }}
          >
            {t("reset_password.subtitle")}
          </Typography>
        </Box>
        <Formik
          validationSchema={schema}
          initialValues={initialValues}
          enableReinitialize
          onSubmit={async (values) => {
            await fetchApi(API_URL + "/userbase/reset-password/", {
              method: "POST",
              body: JSON.stringify({
                ...values,
                token,
              }),
              responseType: "raw",
              headers: {
                "Content-Type": "application/json",
              },
            }).then(() => {
              setConfirm(true)
            })
          }}
        >
          {({ handleSubmit, errors, values, setFieldValue, isSubmitting, isValid }) => (
            <form onSubmit={handleSubmit} style={{ width: "100%" }} noValidate>
              <Box display="flex" flexDirection="column" alignItems="center" width="100%" gap={1} marginBottom={4}>
                {!confirm && (
                  <>
                    <TextField
                      label={t("reset_password.password")}
                      type="password"
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      value={values.password}
                      onChange={(e) => setFieldValue("password", e.target.value)}
                      required
                      helperText={getIn(errors, "password")}
                      error={getIn(errors, "password") ? true : false}
                    />
                    <TextField
                      label={t("reset_password.confirm_password")}
                      type="password"
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      value={values.repeatPassword}
                      onChange={(e) => setFieldValue("repeatPassword", e.target.value)}
                      required
                      helperText={getIn(errors, "repeatPassword")}
                      error={getIn(errors, "repeatPassword") ? true : false}
                    />
                  </>
                )}
                {confirm && (
                  <Alert severity="success" sx={{ width: "100%" }}>
                    {t("reset_password.success_reset")}
                  </Alert>
                )}
              </Box>
              {confirm ? (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  disabled={isSubmitting}
                  onClick={() => {
                    window.location.href = "/login"
                  }}
                >
                  {t("reset_password.cta_login")}
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  loading={isSubmitting}
                  disabled={!values.repeatPassword || !values.password || !isValid || isSubmitting}
                  fullWidth
                >
                  {t("reset_password.cta_reset")}
                </Button>
              )}
            </form>
          )}
        </Formik>
      </Box>
    </Box>
  )
}
